/**
 * Os procedimentos de uma guia — o nível abaixo dela.
 *
 * A guia inteira diz **que houve** glosa; só o item diz **onde**. É a
 * diferença entre "esta guia veio R$ 6,00 a menos" e "o colesterol LDL foi
 * glosado porque está contido no procedimento principal" — a segunda é
 * acionável, a primeira não.
 *
 * Aberto pelo botão na linha da guia, dentro do detalhe do protocolo.
 */
import { useEffect, useState } from 'react';
import { mensagemErro } from '../api/axios';
import { itensDaGuia } from '../api/fluxfin';
import { Alerta, Badge, Botao, LoadingSpinner, Tabela, Th } from './common';
import { brl, formatDate, numero } from '../utils/formato';
import { useOrdenacao } from '../hooks/useOrdenacao';

export default function DetalhesDaGuia({ guia, aoFechar }) {
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState('');
  const [carregando, setCarregando] = useState(true);
  const ord = useOrdenacao(dados?.itens);

  useEffect(() => {
    setCarregando(true);
    itensDaGuia(guia)
      .then((r) => setDados(r.data))
      .catch((e) => setErro(mensagemErro(e, 'Não foi possível carregar os itens.')))
      .finally(() => setCarregando(false));
  }, [guia]);

  return (
    <>
      <div
        className="modal-backdrop fade show"
        onClick={aoFechar}
        // Acima do modal do protocolo, que também usa backdrop.
        style={{ background: 'rgba(16,24,40,.5)', zIndex: 1060 }}
      />
      <div
        className="modal fade show d-block"
        tabIndex="-1"
        role="dialog"
        style={{ zIndex: 1061 }}
      >
        <div className="modal-dialog modal-xl modal-dialog-scrollable modal-dialog-centered">
          <div
            className="modal-content"
            style={{ borderRadius: 16, border: '1px solid var(--ff-border)' }}
          >
            <div className="modal-header" style={{ borderColor: 'var(--ff-border-soft)' }}>
              <div>
                <h2 className="ff-painel__titulo">Guia {guia}</h2>
                <p className="ff-painel__sub">
                  {dados
                    ? `${numero(dados.qtd_itens)} procedimento(s) · ${brl(dados.total_pago)} pago` +
                      (Number(dados.total_glosado) > 0
                        ? ` · ${brl(dados.total_glosado)} glosado`
                        : '')
                    : 'Procedimentos desta guia'}
                </p>
              </div>
              <button className="btn-close" onClick={aoFechar} aria-label="Fechar" />
            </div>

            <div className="modal-body p-0">
              <Alerta tipo="erro">{erro}</Alerta>
              {carregando ? (
                <LoadingSpinner />
              ) : (
                <>
                  <Tabela estreita>
                    <thead>
                      <tr>
                        <Th campo="codigo_servico" estado={ord}>Código</Th>
                        <Th campo="descricao_servico" estado={ord}>Procedimento</Th>
                        <Th campo="data_atendimento" estado={ord}>Atendimento</Th>
                        <Th campo="quantidade" estado={ord} alinhar="right">Qtd</Th>
                        <Th campo="valor_unitario" estado={ord} alinhar="right">Unitário</Th>
                        <Th campo="valor_pago" estado={ord} alinhar="right">Pago</Th>
                        <Th campo="valor_glosado" estado={ord} alinhar="right">Glosado</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {ord.ordenados.map((i, idx) => (
                        <tr key={`${i.codigo_servico}-${idx}`}>
                          <td className="col-codigo">{i.codigo_servico || '—'}</td>
                          <td>
                            {i.descricao_servico || '—'}
                            {i.glosas.length > 0 && (
                              <div style={{ fontSize: 12, color: '#9a5b06', marginTop: 2 }}>
                                {i.glosas.map((c, k) => (
                                  <div key={k}>
                                    <i className="bi bi-scissors me-1" aria-hidden="true" />
                                    <span className="ff-mono">{c.codigo_causa || '—'}</span>
                                    {c.codigo_tiss ? ` · TISS ${c.codigo_tiss}` : ''} —{' '}
                                    {c.motivo}
                                  </div>
                                ))}
                              </div>
                            )}
                          </td>
                          <td className="col-meta">{formatDate(i.data_atendimento)}</td>
                          <td className="col-qtd">{i.quantidade ?? '—'}</td>
                          <td className="col-qtd">{brl(i.valor_unitario)}</td>
                          <td className="col-valor">{brl(i.valor_pago)}</td>
                          <td
                            className="col-qtd"
                            style={Number(i.valor_glosado) > 0 ? { color: '#9a5b06' } : undefined}
                          >
                            {Number(i.valor_glosado) > 0 ? brl(i.valor_glosado) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <th colSpan={5}>Total — {numero(dados?.qtd_itens)} procedimentos</th>
                        <th className="col-valor">{brl(dados?.total_pago)}</th>
                        <th className="col-qtd" style={{ color: '#9a5b06' }}>
                          {Number(dados?.total_glosado) > 0 ? brl(dados.total_glosado) : '—'}
                        </th>
                      </tr>
                    </tfoot>
                  </Tabela>

                  {/* Glosa cujo procedimento não casou com nenhuma linha lida.
                      Aparece à parte em vez de sumir: um valor glosado que o
                      sistema não sabe onde encaixar é justamente o que merece
                      ser olhado. */}
                  {dados?.glosas_sem_item?.length > 0 && (
                    <div className="p-3">
                      <div className="ff-aviso ff-aviso--atencao" style={{ margin: 0 }}>
                        <i className="bi bi-question-circle" aria-hidden="true" />
                        <div>
                          <strong>
                            {dados.glosas_sem_item.length} glosa(s) sem procedimento
                            correspondente
                          </strong>{' '}
                          nesta guia. O código glosado não bate com nenhum item extraído do
                          demonstrativo de pagamento — pode ser divergência de cadastro
                          entre os dois documentos.
                          <ul className="mt-2 mb-0">
                            {dados.glosas_sem_item.map((o) => (
                              <li key={o.codigo_servico}>
                                <span className="ff-mono">{o.codigo_servico || '—'}</span>{' '}
                                — {o.causas.map((c) => c.motivo).join(' · ')}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                  {!carregando && (dados?.itens || []).length === 0 && (
                    <p className="p-4 m-0" style={{ color: 'var(--ff-muted)' }}>
                      Nenhum procedimento extraído para esta guia. Isso acontece quando o
                      arquivo consolida por guia sem detalhar os itens.
                    </p>
                  )}
                </>
              )}
            </div>

            <div className="modal-footer" style={{ borderColor: 'var(--ff-border-soft)' }}>
              {Number(dados?.total_glosado) > 0 && (
                <Badge tom="reprovado">{brl(dados.total_glosado)} glosados</Badge>
              )}
              <Botao variante="secundario" pequeno onClick={aoFechar}>
                Fechar
              </Botao>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
